import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { SpanCol } from "skedyul-ui/ui";
import { store } from "@store/index";
import {
  updateParams,
  close as closeSideNavigation,
} from "@store/features/layout/sideNavigationSlice";
import { fetchCRM } from "@store/features/crm/crmSlice";
import { toast } from "sonner";
import { trpc } from "@utils/api";
import { buildListHref } from "@schemas/pages/helpers/pagePaths";

type UniqueDuplicateSample = {
  value: unknown;
  count: number;
};

type SchemaImpact = {
  operationType: string;
  resourceType: string;
  resourceHandle: string;
  affectedRecords?: number;
  message?: string;
  isDestructive: boolean;
  isBlocked?: boolean;
  samples?: UniqueDuplicateSample[];
  instanceIds?: string[];
  listPath?: string;
  modelHandle?: string;
  fieldHandle?: string;
};

type ParsedSchema = {
  name?: string;
  version?: string;
  models: Array<{
    handle: string;
    name: string;
    namePlural?: string;
    fields?: Array<{ handle: string }>;
  }>;
};

type SchemaMigrationPreviewParams = {
  isLoading?: boolean;
  isApplying?: boolean;
  error?: string;
  schema?: ParsedSchema;
  schemaName?: string;
  schemaVersion?: string;
  impacts?: SchemaImpact[];
  hasChanges?: boolean;
  requiresApproval?: boolean;
  fileId?: string;
  fileName?: string;
};

const getOperationIcon = (operationType: string): string => {
  if (operationType.startsWith("CREATE")) return "Plus";
  if (operationType.startsWith("UPDATE")) return "Pencil";
  if (operationType.startsWith("DELETE")) return "Trash";
  return "Circle";
};

const getOperationLabel = (operationType: string): string => {
  return operationType
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

const getResourceTypeLabel = (resourceType: string): string => {
  return (
    resourceType.charAt(0).toUpperCase() + resourceType.slice(1).toLowerCase()
  );
};

const formatDuplicateSampleValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(", ");
  }
  if (value == null) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const blockedImpactDescription = (impact: SchemaImpact): string => {
  const base =
    impact.affectedRecords && impact.affectedRecords > 0
      ? `${impact.message || "Change is blocked"} (${impact.affectedRecords} duplicate${impact.affectedRecords !== 1 ? "s" : ""})`
      : impact.message ||
        `${getResourceTypeLabel(impact.resourceType)} change is blocked`;

  const sampleLines = (impact.samples ?? [])
    .map((sample) => {
      const label = formatDuplicateSampleValue(sample.value);
      return label
        ? `${label} (${sample.count} record${sample.count !== 1 ? "s" : ""})`
        : null;
    })
    .filter((line): line is string => line != null);

  return sampleLines.length > 0 ? `${base}\n${sampleLines.join("\n")}` : base;
};

export const schemaMigrationPreviewSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }> & {
    props: SchemaMigrationPreviewParams;
  },
): SideNavigationV2Props<PageV2Component> => {
  const { sideNavigation } = args.state;
  const isOpen = sideNavigation[args.position]?.isOpen ?? true;

  const params = sideNavigation[args.position]
    ?.params as SchemaMigrationPreviewParams;
  const isLoading = params?.isLoading ?? false;
  const isApplying = params?.isApplying ?? false;
  const error = params?.error;
  const schema = params?.schema;
  const schemaName = params?.schemaName || "CRM Schema";
  const schemaVersion = params?.schemaVersion;
  const impacts = params?.impacts || [];
  const hasChanges = params?.hasChanges ?? false;
  const fileId = params?.fileId;

  const blockedImpacts = impacts.filter((i) => i.isBlocked);
  const destructiveImpacts = impacts.filter(
    (i) => i.isDestructive && !i.isBlocked,
  );
  const nonDestructiveImpacts = impacts.filter(
    (i) => !i.isDestructive && !i.isBlocked,
  );

  const subdomain = args.routerContext.params.subdomain;

  const handleReviewDuplicates = (impact: SchemaImpact) => {
    if (!subdomain || !impact.listPath || !impact.instanceIds?.length) {
      return;
    }
    const search = new URLSearchParams({
      filters: JSON.stringify({ id: { in: impact.instanceIds } }),
    });
    store.dispatch(closeSideNavigation({ position: args.position }));
    args.routerContext.router.push(
      buildListHref(subdomain, impact.listPath, search),
    );
  };

  const handleApplyMigration = async () => {
    store.dispatch(
      updateParams({
        position: args.position,
        params: { ...params, isApplying: true },
      }),
    );

    try {
      if (!fileId) {
        throw new Error("Missing file ID");
      }

      // Use tRPC to apply the migration
      const result = await trpc.crm.applySchemaMigration.mutate({
        fileId,
      });

      if (!result.success) {
        throw new Error(result.error || "Failed to apply migration");
      }

      toast.success("Migration applied successfully");
      store.dispatch(closeSideNavigation({ position: args.position }));
      void store.dispatch(fetchCRM());
    } catch (err) {
      store.dispatch(
        updateParams({
          position: args.position,
          params: {
            ...params,
            isApplying: false,
            error:
              err instanceof Error ? err.message : "Failed to apply migration",
          },
        }),
      );
    }
  };

  const loadingContent = isLoading
    ? [
        {
          type: "Card" as const,
          title: "Loading",
          props: {
            variant: "outline" as const,
            header: {
              title: "Analyzing schema...",
              description:
                "Please wait while we calculate the migration impact.",
            },
          },
        },
      ]
    : [];

  const errorContent = error
    ? [
        {
          type: "Card" as const,
          title: "Error",
          props: {
            variant: "outline" as const,
            header: {
              title: "Error",
              description: error,
            },
          },
        },
      ]
    : [];

  const noChangesContent =
    !isLoading && !error && impacts.length === 0
      ? [
          {
            type: "Card" as const,
            title: "No Changes",
            props: {
              variant: "outline" as const,
              header: {
                title: "No changes detected",
                description:
                  "The uploaded schema matches the current CRM configuration. No migration is needed.",
              },
            },
          },
        ]
      : [];

  const schemaInfoContent =
    !isLoading && impacts.length > 0
      ? [
          {
            type: "Card" as const,
            title: "Schema Info",
            props: {
              variant: "outline" as const,
              header: {
                title: schemaName,
                description: schemaVersion
                  ? `Version ${schemaVersion}`
                  : "No version specified",
              },
            },
          },
        ]
      : [];

  const blockedContent =
    !isLoading && blockedImpacts.length > 0
      ? [
          {
            type: "Card" as const,
            title: "Cannot Apply",
            props: {
              variant: "outline" as const,
              header: {
                title: `Cannot apply (${blockedImpacts.length})`,
                description:
                  "These changes are blocked until duplicate values are resolved. Other changes can still be applied.",
              },
              form: {
                formVersion: "v1" as const,
                fields: blockedImpacts.flatMap((impact, index) => {
                  const alertField = {
                    id: `blocked-${index}`,
                    component: "Alert" as const,
                    span: 12 as SpanCol,
                    mdSpan: 12 as SpanCol,
                    lgSpan: 12 as SpanCol,
                    props: {
                      icon: "Ban",
                      title: `${getOperationLabel(impact.operationType)}: ${impact.resourceHandle}`,
                      description: blockedImpactDescription(impact),
                      variant: "destructive" as const,
                    },
                  };

                  if (!impact.listPath || !impact.instanceIds?.length) {
                    return [alertField];
                  }

                  return [
                    alertField,
                    {
                      id: `blocked-review-${index}`,
                      component: "Button" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        label: `Review duplicates (${impact.instanceIds.length})`,
                        variant: "outline" as const,
                        className: "w-full",
                        handleClick: () => {
                          handleReviewDuplicates(impact);
                        },
                      },
                    },
                  ];
                }),
              },
            },
          },
        ]
      : [];

  const destructiveContent =
    !isLoading && destructiveImpacts.length > 0
      ? [
          {
            type: "Card" as const,
            title: "Destructive Changes",
            props: {
              variant: "outline" as const,
              header: {
                title: `Destructive Changes (${destructiveImpacts.length})`,
                description:
                  "These changes will permanently delete data and cannot be undone.",
              },
              form: {
                formVersion: "v1" as const,
                fields: destructiveImpacts.map((impact, index) => ({
                  id: `destructive-${index}`,
                  component: "Alert" as const,
                  span: 12 as SpanCol,
                  mdSpan: 12 as SpanCol,
                  lgSpan: 12 as SpanCol,
                  props: {
                    icon: getOperationIcon(impact.operationType),
                    title: `${getOperationLabel(impact.operationType)}: ${impact.resourceHandle}`,
                    description:
                      impact.affectedRecords && impact.affectedRecords > 0
                        ? `${impact.affectedRecords} record${impact.affectedRecords !== 1 ? "s" : ""} will be affected`
                        : impact.message ||
                          `${getResourceTypeLabel(impact.resourceType)} will be deleted`,
                    variant: "destructive" as const,
                  },
                })),
              },
            },
          },
        ]
      : [];

  const nonDestructiveContent =
    !isLoading && nonDestructiveImpacts.length > 0
      ? [
          {
            type: "Card" as const,
            title: "Changes",
            props: {
              variant: "outline" as const,
              header: {
                title: `Changes (${nonDestructiveImpacts.length})`,
                description: "These changes will be applied to your CRM.",
              },
              form: {
                formVersion: "v1" as const,
                fields: nonDestructiveImpacts.map((impact, index) => ({
                  id: `change-${index}`,
                  component: "Alert" as const,
                  span: 12 as SpanCol,
                  mdSpan: 12 as SpanCol,
                  lgSpan: 12 as SpanCol,
                  props: {
                    icon: getOperationIcon(impact.operationType),
                    title: `${getOperationLabel(impact.operationType)}: ${impact.resourceHandle}`,
                    description:
                      impact.message ||
                      `${getResourceTypeLabel(impact.resourceType)} will be ${impact.operationType.startsWith("CREATE") ? "created" : "updated"}`,
                    variant: "default" as const,
                  },
                })),
              },
            },
          },
        ]
      : [];

  const actionsContent =
    !isLoading && impacts.length > 0
      ? [
          {
            type: "Card" as const,
            title: "Actions",
            props: {
              variant: "outline" as const,
              form: {
                formVersion: "v1" as const,
                fields: [
                  {
                    id: "cancel-btn",
                    component: "Button" as const,
                    span: 6 as SpanCol,
                    mdSpan: 6 as SpanCol,
                    lgSpan: 6 as SpanCol,
                    props: {
                      label: "Cancel",
                      variant: "outline" as const,
                      className: "w-full",
                      handleClick: () => {
                        store.dispatch(
                          closeSideNavigation({ position: args.position }),
                        );
                      },
                    },
                  },
                  {
                    id: "apply-btn",
                    component: "Button" as const,
                    span: 6 as SpanCol,
                    mdSpan: 6 as SpanCol,
                    lgSpan: 6 as SpanCol,
                    props: {
                      label: isApplying
                        ? "Applying..."
                        : destructiveImpacts.length > 0
                          ? "Apply (Destructive)"
                          : "Apply Migration",
                      variant:
                        destructiveImpacts.length > 0
                          ? ("destructive" as const)
                          : ("default" as const),
                      className: "w-full",
                      isLoading: isApplying,
                      handleClick: () => {
                        void handleApplyMigration();
                      },
                    },
                  },
                ],
              },
            },
          },
        ]
      : [];

  return {
    side: args.position,
    isOpen,
    width: args.width || "600px",
    component: {
      component: "PageV2",
      props: {
        title: "Migration Preview",
        hideTitle: false,
        header: {
          icon: "Upload",
        },
        content: {
          structure: "sidebar",
          components: [
            ...loadingContent,
            ...errorContent,
            ...noChangesContent,
            ...schemaInfoContent,
            ...actionsContent,
            ...blockedContent,
            ...destructiveContent,
            ...nonDestructiveContent,
          ],
        },
      },
    },
  };
};
